import { useEffect, useMemo, useState } from 'react'
import { getTrends, getDistincts } from '@services/payrollService'
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, Area } from 'recharts'
import Card from '@components/ui/Card'
import MultiSelectAutocomplete from '@components/ui/MultiSelectAutocomplete'
import Loading from '@components/ui/Loading'
import { useGlobalFilters } from '@contexts/FilterContext'

export default function AnalyticsPage() {
  const { indicator, setIndicator } = useGlobalFilters()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [dims, setDims] = useState({ situacoes: [] })

  // Filtros pendentes (selecionados mas ainda não aplicados)
  const [pendingFromMonth, setPendingFromMonth] = useState('')
  const [pendingToMonth, setPendingToMonth] = useState('')
  const [pendingQuickMonths, setPendingQuickMonths] = useState(12)
  const [pendingSituacao, setPendingSituacao] = useState([])

  // Filtros aplicados (usados nas queries)
  const [appliedFromMonth, setAppliedFromMonth] = useState('')
  const [appliedToMonth, setAppliedToMonth] = useState('')
  const [appliedQuickMonths, setAppliedQuickMonths] = useState(12)
  const [appliedSituacao, setAppliedSituacao] = useState([])

  useEffect(() => {
    const abort = new AbortController()
    let active = true
    async function load() {
      try {
        setLoading(true)
        // ensure situacao options
        if (!dims.situacoes?.length) {
          try {
            const ds = await getDistincts({ limit: 500, use_cache: 1 }, abort.signal)
            if (active) setDims({ situacoes: ds?.situacoes || [] })
          } catch {}
        }
        const query = {}
        if (appliedFromMonth) query.from = appliedFromMonth
        if (appliedToMonth) query.to = appliedToMonth
        if (appliedSituacao?.length) query.situacao = appliedSituacao
        const monthsParam = query.from || query.to ? undefined : (appliedQuickMonths || 12)
        const t = await getTrends(query.from || query.to ? query : { months: monthsParam, ...query }, abort.signal)
        const rows = (t.months || []).map((m, i) => ({
          month: m,
          gross: t.gross?.[i] || 0,
          deductions: t.deductions?.[i] || 0,
          net: t.net?.[i] || 0,
          employees: t.employees?.[i] || 0,
          avg_gross: t.avg_gross?.[i] || 0,
          avg_net: t.avg_net?.[i] || 0,
        }))
        if (!active) return
        setData(rows)
      } catch (e) {
        if (abort.signal.aborted || e?.name === 'AbortError') return
        if (!active) return
        setError(e.message)
      } finally {
        if (active) setLoading(false)
      }
    }
    load()
    return () => {
      active = false
      abort.abort()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFromMonth, appliedToMonth, appliedSituacao, appliedQuickMonths])

  // Funções para gerenciar filtros
  const applyFilters = () => {
    setAppliedFromMonth(pendingFromMonth)
    setAppliedToMonth(pendingToMonth)
    setAppliedQuickMonths(pendingQuickMonths)
    setAppliedSituacao(pendingSituacao)
  }

  const clearFilters = () => {
    setPendingFromMonth('')
    setPendingToMonth('')
    setPendingQuickMonths(12)
    setPendingSituacao([])
    setAppliedFromMonth('')
    setAppliedToMonth('')
    setAppliedQuickMonths(12)
    setAppliedSituacao([])
  }

  const hasUnappliedChanges = useMemo(() => {
    return (
      pendingFromMonth !== appliedFromMonth ||
      pendingToMonth !== appliedToMonth ||
      pendingQuickMonths !== appliedQuickMonths ||
      JSON.stringify(pendingSituacao) !== JSON.stringify(appliedSituacao)
    )
  }, [pendingFromMonth, appliedFromMonth, pendingToMonth, appliedToMonth, pendingQuickMonths, appliedQuickMonths, pendingSituacao, appliedSituacao])

  const series = useMemo(() => {
    const indKey = indicator || 'net'
    const mapped = data.map((r, idx, arr) => {
      const raw = r[indKey]
      const valNum = typeof raw === 'number' ? raw : Number(raw || 0)
      const val = Number.isFinite(valNum) ? valNum : 0
      const first = arr[0]?.[indKey] || 0
      const prev = arr[idx - 1]?.[indKey] || null
      const prevNum = typeof prev === 'number' ? prev : Number(prev || 0)
      const firstNum = typeof first === 'number' ? first : Number(first || 0)
      const mom = prevNum && prevNum !== 0 ? ((val - prevNum) / prevNum) * 100 : null
      const since = firstNum && firstNum !== 0 ? ((val - firstNum) / firstNum) * 100 : null
      return { ...r, value: val, mom_pct: mom, since_start_pct: since }
    })
    return mapped
  }, [data, indicator])

  const yDomain = useMemo(() => {
    if (series.length === 0) return [0, 'auto']

    const values = series.map(d => d.value).filter(v => typeof v === 'number' && v > 0)
    if (values.length === 0) return [0, 'auto']

    const minValue = Math.min(...values)
    const maxValue = Math.max(...values)

    // Adiciona padding
    const paddingBelow = minValue * 0.10
    const paddingAbove = maxValue * 0.12

    const rangeMin = minValue - paddingBelow
    const rangeMax = maxValue + paddingAbove
    const range = rangeMax - rangeMin

    // Calcula um intervalo "bonito" baseado na magnitude dos dados
    const magnitude = Math.pow(10, Math.floor(Math.log10(range)))
    const normalized = range / magnitude

    // Escolhe um step size agradável (1, 2, 5, ou 10 vezes a magnitude)
    let niceStep
    if (normalized <= 1.5) niceStep = 0.2 * magnitude
    else if (normalized <= 3) niceStep = 0.5 * magnitude
    else if (normalized <= 7) niceStep = 1 * magnitude
    else niceStep = 2 * magnitude

    // Arredonda os limites para múltiplos do step
    const lowerBound = Math.floor(rangeMin / niceStep) * niceStep
    const upperBound = Math.ceil(rangeMax / niceStep) * niceStep

    const safeLower = lowerBound > 0 ? lowerBound : 0

    return [safeLower, upperBound]
  }, [series])

  const indLabel = useMemo(() => {
    switch (indicator) {
      case 'gross': return 'Bruto'
      case 'net': return 'Líquido'
      case 'avg_gross': return 'Médio Bruto'
      case 'avg_net': return 'Médio Líquido'
      case 'employees': return 'Pessoas'
      default: return 'Indicador'
    }
  }, [indicator])
  const isMoney = indicator !== 'employees'

  // Helpers para eixos compactos
  const fmtCompact = (v) => Number(v).toLocaleString('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })
  const fmtMoneyCompact = (v) => `R$ ${Number(v).toLocaleString('pt-BR', { notation: 'compact', maximumFractionDigits: 1 })}`
  const fmtMonth = (s) => {
    if (!s || typeof s !== 'string' || s.length < 7) return s
    const yy = s.slice(2,4)
    const mm = s.slice(5,7)
    return `${mm}/${yy}`
  }

  if (loading) return <Loading />
  if (error) return <div className="p-4 text-red-600">Erro: {error}</div>

  return (
    <div className="p-4 space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Análises</h2>
        <p className="text-gray-600">Tendências históricas por mês</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <div className="text-xs text-gray-500 mb-1">Indicador</div>
          <select className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            value={indicator}
            onChange={(e)=> setIndicator(e.target.value)}
          >
            <option value="gross">Folha Bruta (total)</option>
            <option value="net">Folha Líquida (total)</option>
            <option value="avg_gross">Salário Médio Bruto</option>
            <option value="avg_net">Salário Médio Líquido</option>
            <option value="employees">Pessoas (quantidade)</option>
          </select>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Situação</div>
          <MultiSelectAutocomplete
            label=""
            options={dims.situacoes}
            value={pendingSituacao}
            onChange={setPendingSituacao}
          />
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">De</div>
          <div className="flex gap-2">
            <select
              className="w-1/2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={pendingFromMonth ? pendingFromMonth.slice(5, 7) : ''}
              onChange={(e) => {
                const month = e.target.value
                const year = pendingFromMonth ? pendingFromMonth.slice(0, 4) : new Date().getFullYear().toString()
                const newValue = month ? `${year}-${month}` : ''
                setPendingFromMonth(newValue)
                if (pendingQuickMonths !== null) setPendingQuickMonths(null)
              }}
            >
              <option value="">Mês</option>
              <option value="01">Janeiro</option>
              <option value="02">Fevereiro</option>
              <option value="03">Março</option>
              <option value="04">Abril</option>
              <option value="05">Maio</option>
              <option value="06">Junho</option>
              <option value="07">Julho</option>
              <option value="08">Agosto</option>
              <option value="09">Setembro</option>
              <option value="10">Outubro</option>
              <option value="11">Novembro</option>
              <option value="12">Dezembro</option>
            </select>
            <select
              className="w-1/2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={pendingFromMonth ? pendingFromMonth.slice(0, 4) : ''}
              onChange={(e) => {
                const year = e.target.value
                const month = pendingFromMonth ? pendingFromMonth.slice(5, 7) : '01'
                const newValue = year ? `${year}-${month}` : ''
                setPendingFromMonth(newValue)
                if (pendingQuickMonths !== null) setPendingQuickMonths(null)
              }}
            >
              <option value="">Ano</option>
              {Array.from({ length: 11 }, (_, i) => 2020 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1">Até</div>
          <div className="flex gap-2">
            <select
              className="w-1/2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={pendingToMonth ? pendingToMonth.slice(5, 7) : ''}
              onChange={(e) => {
                const month = e.target.value
                const year = pendingToMonth ? pendingToMonth.slice(0, 4) : new Date().getFullYear().toString()
                const newValue = month ? `${year}-${month}` : ''
                setPendingToMonth(newValue)
                if (pendingQuickMonths !== null) setPendingQuickMonths(null)
              }}
            >
              <option value="">Mês</option>
              <option value="01">Janeiro</option>
              <option value="02">Fevereiro</option>
              <option value="03">Março</option>
              <option value="04">Abril</option>
              <option value="05">Maio</option>
              <option value="06">Junho</option>
              <option value="07">Julho</option>
              <option value="08">Agosto</option>
              <option value="09">Setembro</option>
              <option value="10">Outubro</option>
              <option value="11">Novembro</option>
              <option value="12">Dezembro</option>
            </select>
            <select
              className="w-1/2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              value={pendingToMonth ? pendingToMonth.slice(0, 4) : ''}
              onChange={(e) => {
                const year = e.target.value
                const month = pendingToMonth ? pendingToMonth.slice(5, 7) : '01'
                const newValue = year ? `${year}-${month}` : ''
                setPendingToMonth(newValue)
                if (pendingQuickMonths !== null) setPendingQuickMonths(null)
              }}
            >
              <option value="">Ano</option>
              {Array.from({ length: 11 }, (_, i) => 2020 + i).map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Botões de período rápido */}
      <div className="flex gap-2">
        <button
          className={`px-3 py-2 rounded-lg border text-sm ${pendingQuickMonths===6?'bg-primary text-white border-primary':'border-gray-300 dark:border-gray-700'}`}
          onClick={()=>{setPendingFromMonth(''); setPendingToMonth(''); setPendingQuickMonths(6)}}
        >
          Últimos 6m
        </button>
        <button
          className={`px-3 py-2 rounded-lg border text-sm ${pendingQuickMonths===12?'bg-primary text-white border-primary':'border-gray-300 dark:border-gray-700'}`}
          onClick={()=>{setPendingFromMonth(''); setPendingToMonth(''); setPendingQuickMonths(12)}}
        >
          Últimos 12m
        </button>
        <button
          className={`px-3 py-2 rounded-lg border text-sm ${pendingQuickMonths===24?'bg-primary text-white border-primary':'border-gray-300 dark:border-gray-700'}`}
          onClick={()=>{setPendingFromMonth(''); setPendingToMonth(''); setPendingQuickMonths(24)}}
        >
          Últimos 24m
        </button>
      </div>

      {/* Botões de Ação */}
      <div className="flex gap-3 items-center">
        <button
          onClick={applyFilters}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            hasUnappliedChanges
              ? 'bg-primary text-white hover:bg-primary-dark'
              : 'bg-gray-300 text-gray-600 cursor-not-allowed'
          }`}
          disabled={!hasUnappliedChanges}
        >
          Aplicar Filtros
        </button>
        <button
          onClick={clearFilters}
          className="px-4 py-2 rounded-lg font-medium bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
        >
          Limpar Filtros
        </button>
        {hasUnappliedChanges && (
          <span className="text-sm text-orange-600 italic">Filtros não aplicados</span>
        )}
      </div>

      <Card className="h-96">
        {series.length === 0 ? (
          <div className="h-full flex items-center justify-center text-gray-500">
            Nenhum dado para o período selecionado
          </div>
        ) : (
        <>
        <div className="mb-2 text-sm text-gray-600">
          {(() => {
            const start = series[0]?.value || 0
            const end = series[series.length-1]?.value || 0
            const pct = start ? ((end - start) / start) * 100 : 0
            return (
              <span>Variação {indLabel}: <span className={pct>=0? 'text-emerald-600':'text-rose-600'}>{pct.toFixed(2)}%</span> no período</span>
            )
          })()}
        </div>
        <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={series} margin={{ top: 10, right: 12, left: 6, bottom: 6 }}>
            <defs>
              <linearGradient id="indGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6107F3" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#6107F3" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" tickFormatter={fmtMonth} interval="preserveStartEnd" minTickGap={24} tick={{ fontSize: 11 }} />
            <YAxis
              yAxisId="left"
              tickFormatter={fmtMoneyCompact} width={100} tickCount={8} domain={yDomain} padding={{ top: 20, bottom: 20 }}
            />
            <YAxis yAxisId="right" orientation="right" tickFormatter={fmtCompact} width={70} tickCount={10} />
            <Tooltip
              formatter={(value, name, props) => {
                const isPeople = name === 'Pessoas' || name === 'Pessoas (quantidade)'
                if (isPeople) return [Number(value).toLocaleString('pt-BR'), 'Pessoas']
                // demais séries tratadas como monetárias
                return [`R$ ${Number(value).toLocaleString('pt-BR')}`, indLabel]
              }}
              labelFormatter={(label, payload) => {
                const p = payload && payload[0] && payload[0].payload
                const mom = p?.mom_pct
                const since = p?.since_start_pct
                const lines = [`${label}`]
                if (since !== null && since !== undefined) lines.push(`Desde o início: ${since.toFixed(2)}%`)
                if (mom !== null && mom !== undefined) lines.push(`vs mês anterior: ${mom.toFixed(2)}%`)
                return lines.join(' | ')
              }}
            />
            <Legend />
            {/* Linha única conforme indicador selecionado */}
            <Line
              yAxisId={indicator === 'employees' ? 'right' : 'left'}
              type="monotone"
              dataKey={indicator === 'employees' ? 'employees' : indicator}
              name={indLabel}
              stroke="#6107F3"
              strokeWidth={3}
              dot={false}
              isAnimationActive
              animationDuration={700}
            />
          </LineChart>
        </ResponsiveContainer>
        </div>
        </>
        )}
      </Card>
    </div>
  )
}